import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type {
  Audiogram,
  DinTrial,
  PatientInfo,
  QuestionnaireAnswers,
  ScreenerConfig,
  TestResult,
  ToneTrial,
} from "@hearing/shared";
import { api } from "./api.js";
import { emitCompleted } from "./embed.js";

interface ToneData {
  audiogram?: Audiogram;
  toneTrials?: ToneTrial[];
  srtDb?: number;
  trials?: DinTrial[];
  perEar?: Record<string, number>;
}

interface ScreenerState {
  loading: boolean;
  error: string | null;
  config: ScreenerConfig | null;
  patient: PatientInfo;
  setPatient: (p: PatientInfo) => void;
  answers: QuestionnaireAnswers;
  saveQuestionnaire: (answers: QuestionnaireAnswers) => void;
  setToneResult: (data: ToneData) => void;
  result: TestResult | null;
  /** Submit the whole screening to the API (once, at the end of the flow). */
  markCompleted: () => Promise<void>;
}

const Ctx = createContext<ScreenerState | null>(null);

export function ScreenerProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<ScreenerConfig | null>(null);
  const [patient, setPatientState] = useState<PatientInfo>({});
  const [answers, setAnswersState] = useState<QuestionnaireAnswers>({});
  const [result, setResult] = useState<TestResult | null>(null);

  // Refs mirror the latest values so markCompleted never reads stale state.
  const patientRef = useRef<PatientInfo>({});
  const answersRef = useRef<QuestionnaireAnswers>({});
  const toneRef = useRef<ToneData>({});
  const submittedRef = useRef(false);
  const platformRef = useRef<ScreenerConfig["brand"]["platform"]>("hearing-expert");
  // Captured once on mount so it survives any later URL changes in the flow.
  const utmSourceRef = useRef<string | undefined>(readUtmSource());

  useEffect(() => {
    let active = true;
    api
      .getConfig()
      .then((cfg) => {
        if (!active) return;
        // Keep the default Hearing Expert theme during a rolling deploy if the
        // web bundle briefly reaches an older API response without brand data.
        if (cfg.brand) {
          platformRef.current = cfg.brand.platform;
          applyBrand(cfg);
        }
        setConfig(cfg);
        setError(null);
      })
      .catch(() => {
        if (!active) return;
        setError("We couldn't load the hearing check. Please try again.");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const setPatient = useCallback((p: PatientInfo) => {
    patientRef.current = { ...patientRef.current, ...p };
    setPatientState(patientRef.current);
  }, []);

  const saveQuestionnaire = useCallback((a: QuestionnaireAnswers) => {
    answersRef.current = a;
    setAnswersState(a);
  }, []);

  const setToneResult = useCallback((data: ToneData) => {
    toneRef.current = data;
  }, []);

  const markCompleted = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    const tone = toneRef.current;
    try {
      const { category } = await api.createScreening({
        platform: platformRef.current,
        patient: patientRef.current,
        answers: answersRef.current,
        srtDb: tone.srtDb,
        trials: tone.trials,
        perEar: tone.perEar,
        audiogram: tone.audiogram,
        toneTrials: tone.toneTrials,
        utmSource: utmSourceRef.current,
      });
      const full: TestResult = {
        category,
        srtDb: tone.srtDb,
        trials: tone.trials ?? [],
        audiogram: tone.audiogram,
        toneTrials: tone.toneTrials,
      };
      setResult(full);
      emitCompleted({ category, srtDb: tone.srtDb });
    } catch {
      // Allow retry on failure; the results screen shows a generic message.
      submittedRef.current = false;
    }
  }, []);

  const value = useMemo<ScreenerState>(
    () => ({
      loading,
      error,
      config,
      patient,
      setPatient,
      answers,
      saveQuestionnaire,
      setToneResult,
      result,
      markCompleted,
    }),
    [
      loading,
      error,
      config,
      patient,
      setPatient,
      answers,
      saveQuestionnaire,
      setToneResult,
      result,
      markCompleted,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useScreener(): ScreenerState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useScreener must be used within ScreenerProvider");
  return ctx;
}

function readUtmSource(): string | undefined {
  const raw = new URLSearchParams(window.location.search).get("utm_source");
  const value = raw?.trim();
  return value ? value : undefined;
}

function applyBrand(config: ScreenerConfig): void {
  const { brand } = config;
  const root = document.documentElement;
  root.style.setProperty("--primary-color", brand.primaryColor);
  root.style.setProperty("--primary-dark", brand.primaryDark);
  root.style.setProperty("--primary-soft", brand.primarySoft);
  root.style.setProperty("--primary-accent", brand.primaryAccent);
  root.style.setProperty("--primary-rgb", brand.primaryRgb);
  document.title = `${brand.name} Hearing Check`;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", brand.primaryColor);
}
